/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

export enum AppIds {
  CompanyTracker = 'app_company_tracker',
  CompanyTrackerLight = 'app_company_tracker_light',
  PortfolioTracker = 'app_portfolio_tracker',
  Assurance = 'app_assurance',
  MaterialityAssessment = 'app_materiality_assessment',
}

export interface PermissionGroup {
  companyTracker: boolean,
  premium: boolean,
  apps: {
    app_company_tracker: boolean,
    app_portfolio_tracker: boolean,
    app_assurance?: boolean,
    [key: string]: boolean | undefined,
  },
}

const defaultFree: PermissionGroup = {
  companyTracker: false,
  premium: false,
  apps: {
    app_company_tracker: true,
    app_portfolio_tracker: false,
    app_sdgap: false,
    app_assurance: true // Assurance App has no initiative so it does not use permission-groups to check against
  },
}

const companyTrackerLight: PermissionGroup = {
  companyTracker: true,
  premium: false,
  apps: {
    app_company_tracker: true,
    app_portfolio_tracker: false,
  },
}

const companyTrackerPro: PermissionGroup = {
  ...defaultFree,
  premium: true,
  companyTracker: true,
  apps: {
    app_company_tracker: true,
    app_portfolio_tracker: false,
    app_sdgap: true
  },
}

const portfolioTrackerDefault: PermissionGroup = {
  ...defaultFree,
  premium: true,
  companyTracker: true,
  apps: {
    app_company_tracker: false,
    app_portfolio_tracker: true,
    app_sdgap: true
  },
}

const portfolioTrackerPlusCompanyTracker: PermissionGroup = {
  ...companyTrackerPro,
  apps: {
    ...companyTrackerPro.apps,
    app_portfolio_tracker: true
  }
};

export const PERMISSION_GROUPS = {
  FREE: 'free',

  COMPANY_TRACKER_STARTER: 'company_tracker_starter',
  COMPANY_TRACKER_LIGHT: 'company_tracker_light',
  COMPANY_TRACKER_PRO: 'company_tracker_pro',
  COMPANY_TRACKER_ENTERPRISE: 'company_tracker_enterprise',
  COMPANY_TRACKER_ENTERPRISE_AND_PORTFOLIO_TRACKER: 'premium_unlimited_portfolio_tracker',

  PORTFOLIO_TRACKER: 'portfolio_tracker',
  PORTFOLIO_TRACKER_EXCHANGE: 'portfolio_tracker_exchange',

  // LEGACY
  COMPANY_TRACKER_ENTERPRISE_OLD1: 'premium_unlimited',
  COMPANY_TRACKER_PRO_OLD1: 'premium_trial_limit1',
  COMPANY_TRACKER_PRO_OLD2: 'premium_limit5',
  COMPANY_TRACKER_PRO_OLD3: 'premium_limit20',
}

export const CompanyTrackerIds = [
  PERMISSION_GROUPS.COMPANY_TRACKER_PRO_OLD1,
  PERMISSION_GROUPS.COMPANY_TRACKER_PRO_OLD2,
  PERMISSION_GROUPS.COMPANY_TRACKER_PRO_OLD3,
  // Even if this is lower than Company Tracker...
  PERMISSION_GROUPS.COMPANY_TRACKER_STARTER,
  PERMISSION_GROUPS.COMPANY_TRACKER_PRO,
  PERMISSION_GROUPS.COMPANY_TRACKER_ENTERPRISE,
  PERMISSION_GROUPS.COMPANY_TRACKER_ENTERPRISE_OLD1,
  PERMISSION_GROUPS.COMPANY_TRACKER_ENTERPRISE_AND_PORTFOLIO_TRACKER,
] as const;

export const PortfolioTrackerIds = [
  PERMISSION_GROUPS.PORTFOLIO_TRACKER,
  PERMISSION_GROUPS.PORTFOLIO_TRACKER_EXCHANGE,
  PERMISSION_GROUPS.COMPANY_TRACKER_ENTERPRISE_AND_PORTFOLIO_TRACKER,
] as const;

export const CompanyTrackerLightIds = [
  PERMISSION_GROUPS.COMPANY_TRACKER_LIGHT,
] as const;

export const DelegationWithouInitiativeIds = [
  ...CompanyTrackerIds,
  ...CompanyTrackerLightIds,
] as const;

export const getAppFromGroup = (permissionGroup: string | undefined) => {
  if (!permissionGroup) {
    return;
  }

  if (CompanyTrackerLightIds.includes(permissionGroup)) {
    return AppIds.CompanyTrackerLight;
  }

  // Only direct permission, combined permission will be treated as CT
  if (PERMISSION_GROUPS.PORTFOLIO_TRACKER === permissionGroup || PERMISSION_GROUPS.PORTFOLIO_TRACKER_EXCHANGE) {
    return AppIds.PortfolioTracker;
  }

  // Not dealing with assurance yet
  return AppIds.CompanyTracker;
}

// Must return same object reference,
// otherwise will cause infinite updates on root app component
// due to being used in app mapStateToProps call,
export const getPermissionGroupConfiguration: (permissionGroup?: string) => PermissionGroup = (permissionGroup) => {
  switch (permissionGroup) {
    case PERMISSION_GROUPS.PORTFOLIO_TRACKER:
    case PERMISSION_GROUPS.PORTFOLIO_TRACKER_EXCHANGE:
      return portfolioTrackerDefault;
    case PERMISSION_GROUPS.COMPANY_TRACKER_ENTERPRISE_AND_PORTFOLIO_TRACKER:
      return portfolioTrackerPlusCompanyTracker;
    case PERMISSION_GROUPS.COMPANY_TRACKER_PRO_OLD1:
    case PERMISSION_GROUPS.COMPANY_TRACKER_PRO_OLD2:
    case PERMISSION_GROUPS.COMPANY_TRACKER_PRO:
    case PERMISSION_GROUPS.COMPANY_TRACKER_ENTERPRISE:
    case PERMISSION_GROUPS.COMPANY_TRACKER_ENTERPRISE_OLD1:
      return companyTrackerPro;
    case PERMISSION_GROUPS.COMPANY_TRACKER_LIGHT:
      return companyTrackerLight;
    case PERMISSION_GROUPS.FREE:
    default:
      return defaultFree;
  }
}

export const hasGroupAccess = (groupIds: string[] | Readonly<string[]>, permissionGroupId: string | Readonly<string[]>) => {
  const apIds = Array.isArray(permissionGroupId) ? permissionGroupId : [permissionGroupId];
  return groupIds.some(groupId => apIds.some(id => groupId === id));
}

/** @deprecated Use appConfig or features instead */
export const isCTL = (initiative: { permissionGroup?: string }) => {
  return initiative.permissionGroup === PERMISSION_GROUPS.COMPANY_TRACKER_LIGHT;
}
